<template>
    <v-dialog :value="show" width="720" persistent scrollable>
        <div class="he" v-if="media">
            <div class="he-head">
                <div>
                    <div class="he-title">{{ $t('media.hotspot.edit_title') }}</div>
                    <div class="he-sub">{{ media.title }}</div>
                </div>
                <v-btn icon small :disabled="isSaving" @click="close">
                    <v-icon size="20">mdi-close</v-icon>
                </v-btn>
            </div>

            <div class="he-body">
                <!-- ⚠ 超過一個熱區的素材**不讓編輯**，因為這個工具只框一個、
                     存下去會把其他的弄掉（後端也會回 400）。官方的 5 熱區圖本來就
                     因為 `is_mine` 為 false 而看不到入口，這一段是給手動 insert 的
                     多熱區使用者素材用的。 -->
                <div v-if="tooMany" class="he-blocked">
                    <v-icon size="30" color="#e57373">mdi-information-outline</v-icon>
                    <div>{{ $t('media.hotspot.err_too_many', { n: existingCount }) }}</div>
                </div>

                <HotspotPicker v-else-if="media.url" v-model="rect" :src="media.url" />

                <div v-if="submitError" class="he-err">{{ submitError }}</div>
            </div>

            <div class="he-foot">
                <v-btn text class="normal-case" :disabled="isSaving" @click="close">
                    {{ $t('button.cancel') }}
                </v-btn>
                <v-btn color="primary" depressed class="normal-case"
                    :disabled="!canSubmit" :loading="isSaving" @click="submit">
                    {{ $t('button.save') }}
                </v-btn>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import { mediaServices } from '@/services/media';
import HotspotPicker from '@/components/console/diary/media/HotspotPicker';
import { attrsOf, HOTSPOT_MIN_EDGE_RATIO } from './mediaConstants';

/**
 * 編輯找不同圖的熱區 — 見 mangachat-studio-api `docs/plans/MediaLibrary.spec.md` §2.2、
 * ADR 0004。
 *
 * ⚠ **只有自己的素材**（呼叫端要先確認 `is_mine`）：後端對官方素材與別人的素材回 403。
 *
 * ⚠ **編輯時不需要擔心縮圖與 EXIF 轉向**（上傳時要）：老師看的 `media.url` 就是已經
 * 存起來的那張圖，所以在它上面量出來的比例直接乘上 `width`/`height` 就對。用比例只是
 * 為了與上傳共用同一份驗證規則。
 *
 * ⚠ **不會因為引用它的題目而失敗**（字表會）。移動框只是改變學生要點的位置，
 * 任何引用它的題目都還是合法的。所以這個對話框沒有「哪一道題目卡住」的錯誤路徑。
 */
export default {
    name: 'HotspotEditDialog',
    components: { HotspotPicker },
    props: {
        show: { type: Boolean, default: false },
        /** 要編輯的熱區素材。⚠ 呼叫端要先確認 `is_mine` 與 `purpose === 'hotspot'`。 */
        media: { type: Object, default: null },
    },
    data() {
        return {
            rect: null,
            submitError: '',
            isSaving: false,
        };
    },
    computed: {
        existingCount() {
            const t = this.media ? attrsOf(this.media).targets : null;
            return Array.isArray(t) ? t.length : 0;
        },
        tooMany() {
            return this.existingCount > 1;
        },
        canSubmit() {
            if (!this.media || this.isSaving || this.tooMany || !this.rect) {
                return false;
            }
            /// ⚠ 太小的框不能存：學生點不到，而那是靜默的死路。
            return this.rect.w >= HOTSPOT_MIN_EDGE_RATIO
                && this.rect.h >= HOTSPOT_MIN_EDGE_RATIO;
        },
    },
    watch: {
        /**
         * ⚠ `immediate` 不能省：呼叫端用 `v-if` 建立時，元件掛載當下 `show` 已經是
         * true，一般的 watcher 不會觸發，表單就是空的。（同一個坑在 MediaPicker /
         * MediaUploadDialog / MediaEditDialog / WordlistDialog 都踩過。）
         */
        show: {
            immediate: true,
            handler(opened) {
                if (opened) {
                    this.reset();
                }
            },
        },
    },
    methods: {
        reset() {
            this.submitError = '';
            this.isSaving = false;
            /**
             * ⚠ **把現有的框帶進來當起點**，而不是讓老師從空白重畫。
             * 換算是 `HotspotRectValidator.toTargets` 的反運算：像素 ÷ 存起來的尺寸。
             * ⚠ 沒有 `width`/`height` 就不帶（後端也會擋）——算出 NaN 會讓 CSS 整個壞掉。
             */
            const t = this.media ? attrsOf(this.media).targets : null;
            const first = Array.isArray(t) ? t[0] : null;
            const w = this.media && this.media.width;
            const h = this.media && this.media.height;
            this.rect = (first && w && h)
                ? {
                    x: first.pxX / w,
                    y: first.pxY / h,
                    w: first.pxW / w,
                    h: first.pxH / h,
                }
                : null;
        },

        async submit() {
            if (!this.canSubmit) {
                return;
            }
            this.isSaving = true;
            this.submitError = '';
            try {
                const updated = await mediaServices.updateHotspotRect(
                    this.media.media_id, this.rect);
                this.$emit('saved', updated);
                this.$emit('update:show', false);
            } catch (err) {
                const status = err && err.status;
                if (status === 403) {
                    this.submitError = this.$t('media.hotspot.err_forbidden');
                } else if (status === 400) {
                    /// 後端的 400 訊息是英文技術描述，不適合直接給老師看，但要留在
                    /// console 裡可查。這一支的 400 只有「框不合法」與「熱區超過一個」
                    /// 兩種，兩者 UI 都已經先擋過，所以走到這裡多半是資料問題。
                    console.log('updateHotspotRect rejected:', err && err.message);
                    this.submitError = this.$t('media.hotspot.err_rejected');
                } else {
                    this.submitError = this.$t('media.hotspot.err_failed');
                }
            } finally {
                this.isSaving = false;
            }
        },

        close() {
            if (this.isSaving) {
                return;
            }
            this.$emit('update:show', false);
        },
    },
};
</script>

<style scoped>
.he {
    background: #fff;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    max-height: 88vh;
}

.he-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 16px 20px 12px;
    border-bottom: 1px solid #eceff1;
}

.he-title {
    font-size: 17px;
    font-weight: 600;
    color: #263238;
}

.he-sub {
    font-size: 12px;
    color: #78909c;
    margin-top: 2px;
    word-break: break-all;
}

.he-body {
    padding: 16px 20px;
    overflow-y: auto;
}

.he-blocked {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #455a64;
    padding: 20px 0;
    line-height: 1.6;
}

.he-err {
    font-size: 12px;
    color: #e53935;
    margin-top: 10px;
    padding: 8px 10px;
    background: #ffebee;
    border-radius: 4px;
    line-height: 1.5;
}

.he-foot {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 20px;
    border-top: 1px solid #eceff1;
}
</style>
